import {
  Box,
  TextField,
  Chip,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { ArrowDropDown } from '@mui/icons-material'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  useCreateComponent,
  useComponentsByProject,
  useTestCaseComponents,
  useAssignComponentToTestCase,
  useUnassignComponentFromTestCase,
} from './hooks/useComponents'

import styles from './styles.module.scss'
import { componentKeys } from './constants/componentKeys'

const ComponentsSelect = ({ projectId, testCaseId }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('')
  const queryClient = useQueryClient()

  const { data: assignedComponents = [] } = useTestCaseComponents(testCaseId)
  const { data: allComponents = [], isLoading: isLoadingComponents } = useComponentsByProject(projectId)

  const assignMutation = useAssignComponentToTestCase(testCaseId)
  const unassignMutation = useUnassignComponentFromTestCase(testCaseId)
  const createComponentMutation = useCreateComponent({
    onSuccess: () => {
      queryClient.invalidateQueries([componentKeys.projectComponents, projectId])
    },
  })

  const selectedComponents = assignedComponents.map((component) => component.value)

  const getFilteredOptions = () => {
    const input = inputValue.trim().toLowerCase()

    let options = input === ''
      ? allComponents.map((c) => c.value)
      : allComponents
        .filter((c) => c.value.toLowerCase().includes(input))
        .map((c) => c.value)

    options = options.filter((value) => !selectedComponents.includes(value))

    if (
      input &&
      !allComponents.some((c) => c.value.toLowerCase() === input)
    ) {
      options.push(`${inputValue} (New Component)`)
    }

    return options
  }

  const assignComponent = async (componentId) => {
    try {
      await assignMutation.mutateAsync(componentId)
      queryClient.invalidateQueries([componentKeys.testCaseComponents, testCaseId])
    } catch (err) {
      console.error('Assign failed:', err)
    }
  }

  const unassignComponent = async (componentId) => {
    try {
      await unassignMutation.mutateAsync(componentId)
      queryClient.invalidateQueries([componentKeys.testCaseComponents, testCaseId])
    } catch (err) {
      console.error('Unassign failed:', err)
    }
  }

  const createComponent = async (value) => {
    try {
      const component = await createComponentMutation.mutateAsync({
        project_id: projectId,
        value,
      })
      await queryClient.invalidateQueries([componentKeys.projectComponents, projectId])
      return component
    } catch (err) {
      console.error('Component creation failed:', err)
      return null
    }
  }

  const handleComponentInput = async (value, isNew = false) => {
    if (selectedComponents.includes(value)) return

    let component = allComponents.find((c) => c.value === value)

    if (!component && isNew) {
      component = await createComponent(value)
      if (!component) return
    }

    if (component) {
      await assignComponent(component.id)
    }
  }

  const handleChange = async (_, newValues) => {
    const lastInput = newValues[newValues.length - 1]

    const removed = selectedComponents.find((c) => !newValues.includes(c))
    if (removed) {
      const componentToRemove = allComponents.find((c) => c.value === removed)
      if (componentToRemove) {
        await unassignComponent(componentToRemove.id)
      }
      return
    }

    let value = lastInput
    let isNew = false

    if (lastInput.endsWith(' (New Component)')) {
      value = lastInput.replace(' (New Component)', '')
      isNew = true
    }

    await handleComponentInput(value, isNew)
    setInputValue('')
  }

  const handleKeyDown = async (e) => {
    if (!open) setOpen(true);
    if (e.key !== 'Enter') return

    const trimmed = inputValue.trim()
    if (!trimmed || selectedComponents.includes(trimmed)) return

    e.preventDefault()
    await handleComponentInput(trimmed, true)
    setInputValue('')
  }

  const handleDelete = async (componentValue) => {
    const component = assignedComponents.find((c) => c.value === componentValue)
    if (component) {
      await unassignComponent(component.id)
    }
  }

  return (
    <Box className={styles.componentsMainContainer}>
      <Autocomplete
        multiple
        freeSolo
        open={open}
        setOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableClearable
        options={getFilteredOptions()}
        value={selectedComponents}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        loading={isLoadingComponents}
        className={styles.detailsAccordionSelect}
        onKeyDown={handleKeyDown}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Components"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingComponents ? <CircularProgress size={20} /> : null}
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      edge="end"
                      disableRipple
                      onClick={() => setOpen((state) => !state)}
                    >
                      <ArrowDropDown />
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }}
          />
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip
              className={styles.detailsAccordionSelectChip}
              label={option}
              {...getTagProps({ index })}
              key={option}
              onDelete={() => handleDelete(option)}
            />
          ))
        }
      />
    </Box>
  )
}

export default ComponentsSelect
